import * as R from 'ramda';
import { useEffect, useState } from 'react';
// helpers/constants
import * as G from '../helpers';
//////////////////////////////////////////////////

const CACHE_KEY = 'places_autocomplete';

const getAllCache = () => {
  try {
    const cached = window.sessionStorage.getItem(CACHE_KEY);

    return cached ? JSON.parse(cached) : {};
  } catch (error) {
    console.error('Error reading cache:', error);

    return {};
  }
};

const getCachedResults = (query) => {
  try {
    const cache = getAllCache();
    const normalizedQuery = R.trim(R.toLower(query));
    const cachedEntry = R.prop(normalizedQuery, cache);

    if (R.isNil(cachedEntry)) return null;

    const { data, timestamp, expiresIn } = cachedEntry;

    const isExpired = Date.now() - timestamp > expiresIn;

    if (isExpired) {
      const updatedCache = R.dissoc(normalizedQuery, cache);

      window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(updatedCache));

      return null;
    }

    return data;
  } catch (error) {
    console.error('Error reading from cache:', error);

    return null;
  }
};

const setCachedResults = (query, data) => {
  try {
    const cache = getAllCache();
    const normalizedQuery = R.trim(R.toLower(query));

    const updatedCache = {
      ...cache,
      [normalizedQuery]: {
        data,
        timestamp: Date.now(),
        expiresIn: 4 * 60 * 60 * 1000, // 4 hours in milliseconds,
      },
    };

    window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(updatedCache));
  } catch (error) {
    console.error('Error writing to cache:', error);
  }
};

const clearExpiredCache = () => {
  try {
    const cache = getAllCache();
    const now = Date.now();

    const validEntries = R.pickBy(({ timestamp, expiresIn }) => (
      now - timestamp <= expiresIn
    ), cache);

    window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(validEntries));
  } catch (error) {
    console.error('Error clearing expired cache:', error);
  }
};

const usePlacesAutocomplete = ({ debounce = 300 } = {}) => {
  'use memo';

  const [ready, setReady] = useState(false);
  const [suggestions, setSuggestions] = useState({ data: [] });

  useEffect(() => {
    if (R.path(['google', 'maps', 'places'], window)) {
      setReady(true);

      clearExpiredCache();
    }
  }, []);

  const clearSuggestions = () => setSuggestions({ data: [] });

  const fetchSuggestions = async (value) => {
    if (R.not(value) || R.not(R.trim(value))) {
      clearSuggestions();

      return;
    }

    if (R.trim(value) && R.path(['google', 'maps', 'places'], window)) {
      const cachedResults = getCachedResults(value);

      if (G.isNotNilAndNotEmpty(cachedResults)) {
        setSuggestions({ data: cachedResults });

        return;
      }

      try {
        const request = {
          input: value,
          includedPrimaryTypes: ['geocode', 'establishment'],
        };

        const res = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

        const { suggestions: autocompleteSuggestions } = res;

        const predictions = autocompleteSuggestions.map((suggestion) => {
          const placePrediction = R.prop('placePrediction', suggestion);
          const text = R.path(['text', 'text'], placePrediction);
          const placeId = R.prop('placeId', placePrediction);
          const structuredFormat = R.prop('structuredFormat', placePrediction);
          const mainText = R.path(['mainText', 'text'], structuredFormat);
          const secondaryText = R.path(['secondaryText', 'text'], structuredFormat);

          return {
            description: R.or(text, ''),
            place_id: R.or(placeId, ''),
            structured_formatting: {
              main_text: R.or(mainText, ''),
              secondary_text: R.or(secondaryText, ''),
            },
          };
        });

        if (G.isNotNilAndNotEmpty(predictions)) {
          setCachedResults(value, predictions);
        }

        setSuggestions({ data: predictions });
      } catch (error) {
        console.error('Error fetching autocomplete suggestions:', error);

        setSuggestions({ data: [] });
      }
    }
  };

  const setValue = G.setDebounce(fetchSuggestions, debounce);

  return {
    ready,
    setValue,
    suggestions,
    clearSuggestions,
  };
};

export default usePlacesAutocomplete;
